import './VideoLibrary.scss';

import classnames from 'classnames';
import React, { useContext } from 'react';
import Debug from 'debug';
import { type VideoEntry, VideoLibrary as VideoLibraryOrganism } from '@bblabs/module-library/organisms';
import { environment } from '../../environment';
import { isViewInComposeMode } from '../abstractions/office';
import { OverlayCoordinatorContext } from '../coordinators/overlay';
import { prependHtmlToEmail } from '../services/bixel/email';
import { MetricService } from '../services/MetricService';
import { getAuthenticatedApiService } from '../services/AuthenticatedApi';
import { userService } from '../services/UserService';
import { Environment } from '../../types/config';
import { getVideoDetails } from '../lib/video';
import { UserContext } from './App';
const debug = Debug('o365:VideoLibrary');

// feature check if this is edgehtml/IE:
// https://stackoverflow.com/questions/56360225/how-to-detect-microsoft-chromium-edge-chredge-edgium-in-javascript/56361977
const isOldEdgeOrIE = !('reversed' in document.createElement('ol'));

export const VideoLibrary = () => {
  const user = useContext(UserContext);
  const overlayContext = useContext(OverlayCoordinatorContext);

  if (!user || !user.token) {
    return null;
  }

  const token = user.token.getAccessToken();
  if (!token) {
    return null;
  }

  async function insertVideo(videoData: VideoEntry) {
    debug('insertVideo()', videoData);

    if (!videoData.video.trueThumbnailUrl) {
      // we have a partial video entry, so ask the server for the full one
      const videoDetails = await getVideoDetails(
        getAuthenticatedApiService(environment.toUpperCase() as Environment, userService),
        videoData.video.id,
        { detailed: true, fixThumbnail: true },
      );

      // patch our partial data with the thumbnail
      if (videoDetails.type === 'detailed') {
        videoData.video.trueThumbnailUrl = videoDetails.trueThumbnailUrl;
      }
    }

    videoData.video.thumbnail = videoData.video.trueThumbnailUrl;
    const thumbnailTarget = document.createElement('img');
    thumbnailTarget.id = videoData.video.id;
    thumbnailTarget.src = videoData.video.trueThumbnailUrl;
    thumbnailTarget.style.maxWidth = '100%';

    const link = document.createElement('a');
    link.href = videoData.video.fullUrl;
    link.target = '_blank';
    link.appendChild(thumbnailTarget);

    try {
      await prependHtmlToEmail(link.outerHTML);
      overlayContext('videoInsert');
    } catch (err) {
      console.error(err);
    }
  }

  const classes = classnames('override', {
    'browser--legacy': isOldEdgeOrIE,
  });

  return (
    <div className="tab--video-library">
      <VideoLibraryOrganism
        appName={MetricService.getAppName()}
        authToken={token}
        className={classes}
        enableCopyMenuItems
        enableFullscreenControl={false}
        environment={environment.toUpperCase() as Uppercase<Environment>}
        insertVideo={insertVideo}
        showInsertButton={isViewInComposeMode()}
        enableVideoEdit={false}
        isRebrand
        showReviewStatus={user.isVideoReviewEnabled}
      />
    </div>
  );
};
